import React, { useEffect, useMemo, useState } from 'react';

import type { AlineText, CodeBlockSuggestionError, SuggestionSource } from '@aline/common';
import { isEditWithSource } from '@aline/common';
import { Collapse, Divider, Typography } from '@mui/material';
import { equals } from 'ramda';
import { Editor, Text } from 'slate';
import { $chatThreadState } from 'src/components/ai/chat/chat-state';
import { SuggestionStatus } from 'src/components/ai/chat/chat-types';
import { useSuggestionGroupNavigationHooks } from 'src/editor/EditorAlert/SuggestionsAlert/useNavigationHooks';
import { useCurrentUserId } from 'src/gql';
import { useDocContentChangedCount, useMaybeEditor } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

import { SuggestionItemExplanation } from './SuggestionItemExplanation';
import { SuggestionItemHeader, SuggestionItemHeaderError } from './SuggestionItemHeader';
import { SuggestionItemSuggestion } from './SuggestionItemSuggestion';
import { useIsAutoScrollChatItemInSuggestions } from './SuggestionList';
import type { SuggestionAndStatus } from '../../../ChatPanelMarkdown';
import { createAIChatItem } from '../../../helpers/aiChatItem';

const useStyles = makeStyles<{ isDocInView: boolean; isSelected: boolean }>()(
  (theme, { isDocInView, isSelected }) => ({
    body: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: theme.spacing(1),
      padding: theme.spacing(1),
    },
    suggestionCard: {
      '&:hover': {
        border: isDocInView
          ? `1px solid ${theme.appColors.primary}`
          : `1px solid ${theme.appColors.rule}`,
      },
      backgroundColor: '#FAFAFC',
      border:
        isSelected && isDocInView
          ? `1px solid ${theme.appColors.primary}`
          : `1px solid ${theme.appColors.rule}`,
      borderRadius: 8,
      boxSizing: 'border-box' as const,
      cursor: isDocInView ? 'pointer' : undefined,
      display: 'flex',
      flexDirection: 'column' as const,
    },
  })
);

export const SuggestionError = React.memo(function SuggestionError({
  error,
}: {
  error: CodeBlockSuggestionError;
}): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(true);
  // Set these both to false so the error card is not interactive.
  const { classes } = useStyles({ isDocInView: false, isSelected: false });

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={classes.suggestionCard}>
      <SuggestionItemHeaderError error={error} handleToggleExpand={handleToggleExpand} />
      <Collapse in={isExpanded}>
        <Divider />
        <div className={classes.body}>
          <Typography variant="body2">{error.message}</Typography>
        </div>
      </Collapse>
    </div>
  );
});

export const SuggestionItem = React.memo(
  function SuggestionItem({
    messageId,
    selectedGroupId,
    suggestionAndStatus,
    threadId,
  }: {
    messageId: string;
    selectedGroupId: string | null;
    suggestionAndStatus: SuggestionAndStatus;
    threadId: UUID;
  }) {
    const editor = useMaybeEditor();
    const currentUserId = useCurrentUserId();
    const { status, suggestion } = suggestionAndStatus;
    const { docContentChangedCount } = useDocContentChangedCount();
    const { classes } = useStyles({
      isDocInView: status !== SuggestionStatus.DocumentNotInView,
      isSelected: selectedGroupId === suggestion.groupId,
    });
    const { chatThreadState, isAutoScrollChatItemInSuggestions } =
      useIsAutoScrollChatItemInSuggestions({
        messageId,
        suggestionIds: [suggestion.groupId],
        threadId,
      });
    const { handleNavigateToGroupBySuggestion } = useSuggestionGroupNavigationHooks();

    // Create a unique ID for this suggestion using the same format as the URL parameter.
    const elementId = createAIChatItem({
      groupId: suggestion.groupId,
      messageId,
      threadId,
      type: 'suggestion',
    });
    const visibilityStateValue = chatThreadState.suggestionVisibilityByGroupId[suggestion.groupId];
    const isSuggestionInDocument = useMemo(() => {
      if (!docContentChangedCount || !editor) return false;

      const textEntriesWithSuggestionSource = Editor.nodes<AlineText>(editor, {
        at: [],
        match: node =>
          Text.isText(node) &&
          !!node.suggestedEdits?.some(edit => {
            return (
              isEditWithSource(edit) &&
              edit.authorId === currentUserId &&
              equals(edit.source, {
                groupId: suggestion.groupId,
                messageId,
                threadId,
                type: 'ai-chat',
              } satisfies SuggestionSource)
            );
          }),
        voids: true,
      });

      return Boolean(textEntriesWithSuggestionSource.next().value);
    }, [currentUserId, docContentChangedCount, editor, messageId, threadId, suggestion]);
    const isSuggestionVisible = useMemo(() => {
      const isOpenByDefaultStatus = getIsOpenByDefaultStatus(status);

      /**
       * If the visibility is set in the chatThreadState, we always use that.
       */
      if (visibilityStateValue !== undefined) return visibilityStateValue === 'visible';

      return isOpenByDefaultStatus;
    }, [status, visibilityStateValue]);

    useEffect(() => {
      if (isAutoScrollChatItemInSuggestions && visibilityStateValue !== 'visible')
        $chatThreadState.suggestionVisibilityByGroupId[suggestion.groupId] = 'visible';
    }, [isAutoScrollChatItemInSuggestions, suggestion.groupId, visibilityStateValue]);

    return (
      <div
        className={classes.suggestionCard}
        id={elementId}
        onClick={() => handleNavigateToGroupBySuggestion(suggestion)}
      >
        <SuggestionItemHeader
          isSuggestionInDocument={isSuggestionInDocument}
          isSuggestionVisible={isSuggestionVisible}
          suggestionAndStatus={suggestionAndStatus}
        />
        <Collapse in={isSuggestionVisible}>
          <Divider />
          <div className={classes.body}>
            <SuggestionItemExplanation suggestion={suggestion} />
            <SuggestionItemSuggestion suggestionAndStatus={suggestionAndStatus} />
          </div>
        </Collapse>
      </div>
    );
  },
  // Deep equality check needed to avoid constant re-renders as streaming happens.
  (prev, next) => equals(prev, next)
);

function getIsOpenByDefaultStatus(status: SuggestionStatus): boolean {
  switch (status) {
    case SuggestionStatus.NotAlineDoc:
    case SuggestionStatus.DocumentNotInView:
      return true;
    default:
      return false;
  }
}
